import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import './TradeGrid.css';
import { exportTradeToExcel } from '../../utils/excelExport';
import { formatAmountDisplay, formatPriceDisplay } from '../../utils/amountFormatter';

interface ToleranceSettings {
  currencyTolerances: { currency: string; tolerance: number }[];
  defaultTolerance: number;
}

interface CptyExpiryCalcGridProps {
  selectedTrades: any[];
  ourCalculations?: any[];
  toleranceSettings?: ToleranceSettings | null;
}

const CptyExpiryCalcGrid: React.FC<CptyExpiryCalcGridProps> = ({ selectedTrades, ourCalculations = [], toleranceSettings }) => {
  const { t } = useTranslation();

  // Don't recalculate - selectedTrades already contains counterparty expiry data with settlement amounts
  const processedTrades = useMemo(() => {
    return selectedTrades.map(trade => trade);
  }, [selectedTrades]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    if (parts[0].length === 4) {
      // YYYY-MM-DD → display as DD-MM-YYYY
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  };

  const locale = i18n.language?.substring(0, 2) || 'es';

  const formatAmount = (value: number | null | undefined) => {
    if (value == null || value === 0) return '—';
    return formatAmountDisplay(value, locale, 2);
  };

  const formatCcyAmount = (ccy: string, value: number | null | undefined) => {
    if (value == null || value === 0) return '—';
    return `${ccy} ${formatAmount(value)}`;
  };

  const formatPrice = (value: number | null | undefined) => {
    if (value == null) return '—';
    return formatPriceDisplay(value, locale, 6);
  };

  // Helper: get tolerance for a currency from admin settings, with fallback defaults
  const getTol = (currency: string): number => {
    if (toleranceSettings) {
      const entry = toleranceSettings.currencyTolerances.find(e => e.currency === currency);
      if (entry) return entry.tolerance;
      return toleranceSettings.defaultTolerance;
    }
    return currency === 'CLP' ? 2 : 0.01;
  };

  // Helper: check if a field was actually extracted from the counterparty email
  // (vs inherited from matched trade data). If extractedFields is absent (old data), show all fields.
  const wasExtracted = (trade: any, field: string): boolean => {
    const fields = trade.extractedFields;
    if (!fields) return true; // Backwards-compatible: show all if tracking not present
    return fields.includes(field);
  };

  // Helper: determine if a counterparty input field differs from our calculation (FR-7.2.8.4)
  // Only highlights differences for fields actually extracted from the counterparty email.
  const getInputDiffStyle = (trade: any, field: string, cptyValue: any): React.CSSProperties => {
    // Don't show diff for inherited (non-extracted) fields
    const fieldMap: Record<string, string> = { 'Direction': 'Direction', 'Amount': 'QuantityCurrency1', 'Price': 'Price', 'FixingPrice': 'FixingPrice', 'SettlementPrice': 'ConversionRate' };
    if (!wasExtracted(trade, fieldMap[field] || field)) return {};

    const ourCalc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
    if (!ourCalc) return {};

    let ourValue: any;
    switch (field) {
      case 'Direction': ourValue = ourCalc.Direction; break;
      case 'Amount': ourValue = ourCalc.QuantityCurrency1; break;
      case 'Price': ourValue = ourCalc.Price; break;
      case 'FixingPrice': ourValue = ourCalc.DisplayFixingPrice || ourCalc.FixingRate || 0; break;
      case 'SettlementPrice': ourValue = ourCalc.SettlementPrice || 0; break;
      default: return {};
    }

    // For numeric fields, compare with tolerance
    if (typeof cptyValue === 'number' && typeof ourValue === 'number') {
      if (Math.abs(cptyValue - ourValue) > 0.001) {
        return { color: '#dc3545', fontWeight: 'bold' };
      }
    } else if (cptyValue !== ourValue) {
      return { color: '#dc3545', fontWeight: 'bold' };
    }

    return {};
  };

  const handleExportRow = async (trade: any) => {
    const ourTrade = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);

    if (!ourTrade) {
      alert('Our calculation not found for this trade');
      return;
    }

    const originalTrade = selectedTrades.find(t => t.TradeNumber === trade.TradeNumber);
    if (!originalTrade) {
      alert('Original trade data not found');
      return;
    }

    exportTradeToExcel(originalTrade, trade);
  };

  if (processedTrades.length === 0) {
    return (
      <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b3b3b3' }}>
        {t('forwardMaturity.selectTradesMessage')}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        color: '#fff',
        fontSize: '12px'
      }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '80px' }}>{t('forwardMaturity.columns.tradeNumber')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '100px' }}>{t('forwardMaturity.columns.product')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.counterparty')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '90px' }}>{t('forwardMaturity.columns.maturityDate')}</th>
            <th style={{ width: '50px' }}></th>
            <th style={{ width: '140px' }}></th>
            <th style={{ width: '80px' }}></th>
            <th style={{ width: '80px' }}></th>
            <th style={{ width: '80px' }}></th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.pagamos')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.recibimos')}</th>
          </tr>
        </thead>
        <tbody>
          {processedTrades.map((trade, index) => {
            // Check if trade has counterparty confirmation
            const isConfirmed = trade.expiryState && trade.expiryState !== 'pending';

            if (!isConfirmed) {
              // Show pending message for unconfirmed trades
              return (
                <tr key={index} id={`cpty-row-${trade.TradeNumber}`} style={{
                  borderBottom: '1px solid var(--border-color)',
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)',
                  color: '#ffc107',
                  fontStyle: 'italic'
                }}>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{trade.TradeNumber}</td>
                  <td style={{ padding: '0.4rem 0.5rem', fontSize: '11px' }}>{trade.ProductLabel || 'Forward'}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{trade.CounterpartyName}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{formatDate(trade.MaturityDate)}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}></td>
                  <td style={{ padding: '0.4rem 0.5rem' }}></td>
                  <td style={{ padding: '0.4rem 0.5rem' }}></td>
                  <td colSpan={4} style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                    {t('forwardMaturity.columns.counterpartyConfirmationPending')}
                  </td>
                </tr>
              );
            }

            // Find matching our calculation
            const ourCalc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
            const isPhysicalDelivery = trade.SettlementType !== 'Compensación';

            // Compare with our calculations for settlement amounts (green/orange/red backgrounds)
            const wePayTol = getTol(trade.WePayCurrency || '');
            const weRecvTol = getTol(trade.WeReceiveCurrency || '');
            const wePayDiff = ourCalc ? Math.abs((trade.WePayAmount || 0) - (ourCalc.WePayAmount || 0)) : Infinity;
            const weRecvDiff = ourCalc ? Math.abs((trade.WeReceiveAmount || 0) - (ourCalc.WeReceiveAmount || 0)) : Infinity;

            // Green = exact match, Orange = within tolerance but different, Red = exceeds tolerance
            const wePayBgColor = wePayDiff === 0 ? '#28a745' : wePayDiff <= wePayTol ? '#e67e00' : '#dc3545';
            const weReceiveBgColor = weRecvDiff === 0 ? '#28a745' : weRecvDiff <= weRecvTol ? '#e67e00' : '#dc3545';

            // Input field diff styles (FR-7.2.8.4 — red/bold for differing inputs)
            // Only applied when the field was actually extracted from the counterparty email
            const dirDiffStyle = getInputDiffStyle(trade, 'Direction', trade.Direction);
            const amtDiffStyle = getInputDiffStyle(trade, 'Amount', trade.QuantityCurrency1);
            const priceDiffStyle = getInputDiffStyle(trade, 'Price', trade.Price);
            const fixDiffStyle = isPhysicalDelivery ? {} : getInputDiffStyle(trade, 'FixingPrice', trade.FixingPrice || trade.DisplayFixingPrice || trade.FixingRate || 0);
            const settDiffStyle = isPhysicalDelivery ? {} : getInputDiffStyle(trade, 'SettlementPrice', trade.SettlementPrice || 0);

            // Determine which input fields were extracted from the email
            const hasDirection = wasExtracted(trade, 'Direction');
            const hasAmount = wasExtracted(trade, 'QuantityCurrency1');
            const hasPrice = wasExtracted(trade, 'Price');
            const hasFixing = wasExtracted(trade, 'FixingPrice');
            const hasConversion = wasExtracted(trade, 'ConversionRate');

            return (
              <tr key={index} id={`cpty-row-${trade.TradeNumber}`} style={{
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)'
              }}>
                <td style={{ padding: '0.4rem 0.5rem' }}>{trade.TradeNumber}</td>
                <td style={{ padding: '0.4rem 0.5rem', fontSize: '11px' }}>{trade.ProductLabel || 'Forward'}</td>
                <td style={{ padding: '0.4rem 0.5rem' }}>{trade.CounterpartyName}</td>
                <td style={{ padding: '0.4rem 0.5rem' }}>{formatDate(trade.MaturityDate)}</td>
                <td style={{ padding: '0.4rem 0.5rem' }}></td>
                <td style={{ padding: '0.4rem 0.5rem' }}></td>
                <td style={{ padding: '0.4rem 0.5rem' }}></td>
                <td style={{ padding: '0.4rem 0.5rem' }}></td>
                <td style={{ padding: '0.4rem 0.5rem' }}></td>
                <td style={{
                  padding: '0.4rem 0.5rem',
                  textAlign: 'right',
                  backgroundColor: wePayBgColor,
                  color: '#fff'
                }}>
                  {formatCcyAmount(trade.WePayCurrency, trade.WePayAmount)}
                </td>
                <td style={{
                  padding: '0.4rem 0.5rem',
                  textAlign: 'right',
                  backgroundColor: weReceiveBgColor,
                  color: '#fff'
                }}>
                  {formatCcyAmount(trade.WeReceiveCurrency, trade.WeReceiveAmount)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {(() => {
            // Group totals by currency for counterparty calculations (only confirmed trades)
            const wePayTotals: { [currency: string]: number } = {};
            const weReceiveTotals: { [currency: string]: number } = {};
            const tradeCountByCurrency: { [currency: string]: number } = {};

            processedTrades.forEach(trade => {
              if (trade.expiryState && trade.expiryState !== 'pending') {
                if (trade.WePayCurrency && trade.WePayAmount != null) {
                  wePayTotals[trade.WePayCurrency] = (wePayTotals[trade.WePayCurrency] || 0) + (trade.WePayAmount || 0);
                }
                if (trade.WeReceiveCurrency && trade.WeReceiveAmount != null) {
                  weReceiveTotals[trade.WeReceiveCurrency] = (weReceiveTotals[trade.WeReceiveCurrency] || 0) + (trade.WeReceiveAmount || 0);
                }
                // Count trades per currency for scaling tolerance on totals
                if (trade.WePayCurrency) {
                  tradeCountByCurrency[trade.WePayCurrency] = (tradeCountByCurrency[trade.WePayCurrency] || 0) + 1;
                }
                if (trade.WeReceiveCurrency && trade.WeReceiveCurrency !== trade.WePayCurrency) {
                  tradeCountByCurrency[trade.WeReceiveCurrency] = (tradeCountByCurrency[trade.WeReceiveCurrency] || 0) + 1;
                }
              }
            });

            // Group totals by currency for our calculations (only confirmed trades)
            const ourWePayTotals: { [currency: string]: number } = {};
            const ourWeReceiveTotals: { [currency: string]: number } = {};

            ourCalculations.forEach(trade => {
              if (trade.expiryState && trade.expiryState !== 'pending') {
                if (trade.WePayCurrency && trade.WePayAmount !== undefined) {
                  ourWePayTotals[trade.WePayCurrency] = (ourWePayTotals[trade.WePayCurrency] || 0) + trade.WePayAmount;
                }
                if (trade.WeReceiveCurrency && trade.WeReceiveAmount !== undefined) {
                  ourWeReceiveTotals[trade.WeReceiveCurrency] = (ourWeReceiveTotals[trade.WeReceiveCurrency] || 0) + trade.WeReceiveAmount;
                }
              }
            });

            const allCurrencies = Array.from(new Set([...Object.keys(wePayTotals), ...Object.keys(weReceiveTotals)])).sort();

            return allCurrencies.map((currency, i) => {
              const cptyWePayTotal = wePayTotals[currency] || 0;
              const cptyWeReceiveTotal = weReceiveTotals[currency] || 0;
              const ourWePayTotal = ourWePayTotals[currency] || 0;
              const ourWeReceiveTotal = ourWeReceiveTotals[currency] || 0;

              // Scale tolerance by number of trades (rounding can accumulate)
              const baseTol = getTol(currency);
              const tolerance = baseTol * (tradeCountByCurrency[currency] || 1);
              const wePayDiff = Math.abs(cptyWePayTotal - ourWePayTotal);
              const weRecvDiff = Math.abs(cptyWeReceiveTotal - ourWeReceiveTotal);

              const wePayBgColor = wePayDiff === 0 ? '#28a745' : wePayDiff <= tolerance ? '#e67e00' : '#dc3545';
              const weReceiveBgColor = weRecvDiff === 0 ? '#28a745' : weRecvDiff <= tolerance ? '#e67e00' : '#dc3545';

              return (
                <tr key={currency} style={{ borderTop: i === 0 ? '2px solid var(--border-color)' : '1px solid var(--border-color)', fontWeight: 'bold' }}>
                  <td colSpan={9} style={{ padding: '0.6rem 0.5rem', textAlign: 'right' }}>
                    {i === 0 ? 'Totals:' : ''}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', backgroundColor: wePayBgColor, color: '#fff' }}>
                    {formatCcyAmount(currency, cptyWePayTotal)}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', backgroundColor: weReceiveBgColor, color: '#fff' }}>
                    {formatCcyAmount(currency, cptyWeReceiveTotal)}
                  </td>
                </tr>
              );
            });
          })()}
        </tfoot>
      </table>
    </div>
  );
};

export default CptyExpiryCalcGrid;
